import { useEffect, useState } from 'react';
import { Grid, Button, Chip, Tooltip, Typography, Autocomplete, TextField } from '@mui/material';
import { TitleTypography, SelectableButton } from '../Components';
import { FormatDate } from '../Functions';
import { DescriptionRounded, TimelineRounded, FiberManualRecord, CheckCircleOutlineRounded } from '@mui/icons-material';
import { OpenInNewRounded } from '@mui/icons-material';
import { isEmpty, isArray, truncate } from 'lodash';
import { blue, green } from '@mui/material/colors';
import StateManager from '../StateManager';
import { createFilterOptions } from '@mui/material/Autocomplete';
import axios from 'axios';
import { FormIcon, ProcessIcon } from '../Icons';

const entryFilterOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (entry) =>
    `${entry.number} ${entry.title} ${
      isArray(entry.refFieldsValues) ? entry.refFieldsValues.map((x) => x.text).join(' ') : ''
    }`,
});

const maxRefValueLength = 30;

const DataSourceTypes = [
  {
    value: 'form',
    text: 'Form',
    icon: <DescriptionRounded />,
  },
  {
    value: 'process',
    text: 'Process',
    icon: <TimelineRounded />,
  },
];

export default function SourceSelector({ onResult, cancelButton }) {
  const [dataSourceType, setDataSourceType] = useState(null);

  // forms

  const [loadingForms, setLoadingForms] = useState(false);
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [loadingFormEntries, setLoadingFormEntries] = useState(false);
  const [formEntries, setFormEntries] = useState([]);
  const [selectedFormEntry, setSelectedFormEntry] = useState(null);
  const needToLoadForms = dataSourceType === 'form' && isEmpty(forms);

  // processes
  const [loadingProcesses, setLoadingProcesses] = useState(false);
  const [processes, setProcesses] = useState([]);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [loadingProcessEntries, setLoadingProcessEntries] = useState(false);
  const [processEntries, setProcessEntries] = useState([]);
  const [selectedProcessEntry, setSelectedProcessEntry] = useState(null);
  const needToLoadProcesses = dataSourceType === 'process' && isEmpty(processes);

  useEffect(() => {
    if (!needToLoadForms) return;

    setLoadingForms(true);

    axios
      .get('/forms/search/getSearchForms')
      .then(({ data }) => setForms(data))
      .catch((err) => StateManager.setAxiosErrorAlert(err))
      .finally(() => setLoadingForms(false));
  }, [needToLoadForms]);

  useEffect(() => {
    if (!needToLoadProcesses) return;

    setLoadingProcesses(true);

    axios
      .get('/process/search/getSearchProcesses')
      .then(({ data }) => setProcesses(data))
      .catch((err) => StateManager.setAxiosErrorAlert(err))
      .finally(() => setLoadingProcesses(false));
  }, [needToLoadProcesses]);

  function loadFormEntries(formId) {
    setLoadingFormEntries(true);
    setFormEntries([]);

    axios
      .get('/forms/search/getSearchFormsEntries', { params: { formId } })
      .then(({ data }) => setFormEntries(data))
      .catch((err) => StateManager.setAxiosErrorAlert(err))
      .finally(() => setLoadingFormEntries(false));
  }

  function loadProcessEntries(processId) {
    setLoadingProcessEntries(true);
    setProcessEntries([]);

    axios
      .get('/process/search/getSearchProcessEntries', { params: { processId } })
      .then(({ data }) => setProcessEntries(data))
      .catch((err) => StateManager.setAxiosErrorAlert(err))
      .finally(() => setLoadingProcessEntries(false));
  }

  function selectFormEntry(entryId) {
    if (!entryId || !selectedForm) return;

    const toData = { entryId, formId: selectedForm._id };
    const result = { to: entryId, toData, toType: 'form-entry' };

    onResult(result);
  }

  function selectProcessEntry(entryId) {
    if (!entryId || !selectedProcess) return;

    const toData = { entryId, formId: selectedProcess._id };
    const result = { to: entryId, toData, toType: 'process-entry' };

    onResult(result);
  }

  return (
    <Grid container>
      <Grid container alignItems="center" sx={{ my: 1 }} spacing={2}>
        <Grid item>
          <Typography variant="h6">Pick an option to create a new link to:</Typography>
        </Grid>
        {DataSourceTypes.map((type, i) => (
          <Grid item key={i}>
            <SelectableButton
              selected={dataSourceType === type.value}
              onClick={() => setDataSourceType(type.value)}
              startIcon={type.icon}
            >
              {type.text}
            </SelectableButton>
          </Grid>
        ))}
        <Grid item sx={{ ml: 'auto' }}>
          {cancelButton}
        </Grid>
      </Grid>

      {dataSourceType === 'form' && (
        <>
          <Grid container alignItems="center" sx={{ my: 1 }}>
            <Autocomplete
              loading={loadingForms}
              options={forms}
              value={selectedForm}
              fullWidth
              getOptionLabel={(option) => option?.title || ''}
              isOptionEqualToValue={(option, value) => option?._id === value?._id}
              openOnFocus
              renderOption={(props, option) => (
                <li {...props} key={option._id}>
                  <Grid container alignItems="center" wrap="nowrap">
                    <FormIcon />
                    <TitleTypography item={option} style={{ marginLeft: '1rem' }} />
                  </Grid>
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Select a form" />}
              onChange={(e, value) => {
                setSelectedForm(value);
                if (value?._id) {
                  loadFormEntries(value?._id);
                } else {
                  setFormEntries([]);
                }
              }}
            />
          </Grid>

          <Grid container alignItems="center" sx={{ my: 1 }}>
            <Autocomplete
              filterOptions={entryFilterOptions}
              options={formEntries}
              value={selectedFormEntry}
              loading={loadingFormEntries}
              fullWidth
              getOptionLabel={(option) => option?.title || ''}
              isOptionEqualToValue={(option, value) => option?._id === value?._id}
              openOnFocus
              renderOption={(props, entry) => (
                <li {...props} key={entry._id}>
                  <Grid container>
                    <Grid container alignItems="center" wrap="nowrap">
                      {entry.completedAt ? (
                        <CheckCircleOutlineRounded sx={{ color: green[500] }} />
                      ) : (
                        <FiberManualRecord sx={{ color: blue[500] }} />
                      )}

                      <Typography sx={{ mx: 2 }} noWrap>
                        #{entry.number} - {entry.title}
                      </Typography>

                      <Typography
                        sx={{ ml: 'auto', color: (theme) => theme.palette.text.secondary, fontSize: '0.9em' }}
                      >
                        {entry.completedAt
                          ? `Completed ${FormatDate(entry.completedAt)}`
                          : `Started ${FormatDate(entry.createdAt)}`}
                      </Typography>

                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`/forms/entry/${entry._id}`, '_blank');
                        }}
                        size="small"
                        sx={{ ml: 1 }}
                        variant="outlined"
                        startIcon={<OpenInNewRounded />}
                      >
                        open in new tab
                      </Button>
                    </Grid>
                    {!isEmpty(entry.refFieldsValues) && isArray(entry.refFieldsValues) && (
                      <Grid container alignItems="center">
                        <Typography sx={{ mr: 1, color: (theme) => theme.palette.text.secondary }}>
                          Ref field data:
                        </Typography>
                        {entry.refFieldsValues.map((item, i) => (
                          <Tooltip title={item.title} key={i}>
                            <Chip
                              size="small"
                              label={truncate(item.text, { length: maxRefValueLength })}
                              sx={{ m: 0.5 }}
                            />
                          </Tooltip>
                        ))}
                      </Grid>
                    )}
                  </Grid>
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Select the entry" />}
              onChange={(e, value) => {
                setSelectedFormEntry(value);
                selectFormEntry(value?._id);
              }}
            />
          </Grid>
        </>
      )}
      {dataSourceType === 'process' && (
        <>
          <Grid container sx={{ py: 1 }}>
            <Autocomplete
              getOptionLabel={(option) => option?.title || ''}
              options={processes}
              loading={loadingProcesses}
              value={selectedProcess}
              renderOption={(props, option) => (
                <li {...props} key={option._id}>
                  <Grid container item alignItems="center" wrap="nowrap">
                    <ProcessIcon />
                    <TitleTypography item={option} style={{ marginLeft: '1rem' }} />
                  </Grid>
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Select a process" />}
              onChange={(e, process) => {
                setSelectedProcess(process);

                if (process) {
                  loadProcessEntries(process._id);
                } else {
                  setProcessEntries([]);
                }
              }}
              fullWidth
            />
          </Grid>

          <Autocomplete
            filterOptions={entryFilterOptions}
            options={processEntries}
            value={selectedProcessEntry}
            loading={loadingProcessEntries}
            fullWidth
            getOptionLabel={(option) => option?.title || ''}
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
            openOnFocus
            renderOption={(props, entry) => (
              <li {...props} key={entry._id}>
                <Grid container>
                  <Grid container alignItems="center" wrap="nowrap">
                    {entry.completedAt ? (
                      <CheckCircleOutlineRounded sx={{ color: green[500] }} />
                    ) : (
                      <FiberManualRecord sx={{ color: blue[500] }} />
                    )}

                    <Typography sx={{ mx: 2 }} noWrap>
                      #{entry.number} - {entry.title}
                    </Typography>

                    <Typography sx={{ ml: 'auto', color: (theme) => theme.palette.text.secondary, fontSize: '0.9em' }}>
                      {entry.completedAt
                        ? `Completed ${FormatDate(entry.completedAt)}`
                        : `Started ${FormatDate(entry.createdAt)}`}
                    </Typography>

                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`/processes/ongoing/${entry._id}`, '_blank');
                      }}
                      size="small"
                      sx={{ ml: 1 }}
                      variant="outlined"
                      startIcon={<OpenInNewRounded />}
                    >
                      open in new tab
                    </Button>
                  </Grid>
                  {!isEmpty(entry.refFieldsValues) && isArray(entry.refFieldsValues) && (
                    <Grid container alignItems="center">
                      <Typography sx={{ mr: 1, color: (theme) => theme.palette.text.secondary }}>
                        Ref field data:
                      </Typography>
                      {entry.refFieldsValues.map((item, i) => (
                        <Tooltip title={item.title} key={i}>
                          <Chip
                            size="small"
                            label={truncate(item.text, { length: maxRefValueLength })}
                            sx={{ m: 0.5 }}
                          />
                        </Tooltip>
                      ))}
                    </Grid>
                  )}
                </Grid>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Select the entry" />}
            onChange={(e, value) => {
              setSelectedProcessEntry(value);
              selectProcessEntry(value?._id);
            }}
          />
        </>
      )}
    </Grid>
  );
}
